import { merge } from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useCallback } from "react";
import { z } from "zod";

import {
  ActBlueImportIntegration,
  ImportCampaignContactsFromActBlueImportMutation,
  ImportCampaignContactsFromActBlueImportMutationVariables,
  ActBlueImportFilterInput,
  ActBlueImportFilter,
  CampaignContactActBlueImportArgs,
} from "~frontend/__generated__/graphql";
import { ACT_BLUE_IMPORT_CAMPAIGN_CONTACT_IMPORT } from "~frontend/graphql/campaigns";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../types";
import ImportForm, {
  ActBlueImportFilterForForm,
  blankActBlueFilter,
} from "./ImportForm";

export default function ActBlueImport({
  campaign,
  availableActBlueImportIntegrations,
  readOnly,
  previousJobArgs,
  displayErrorToast,
  resetContactSourceSettings,
}: {
  campaign: CampaignForEditor;
  availableActBlueImportIntegrations: ActBlueImportIntegration[];
  readOnly?: boolean;
  previousJobArgs?: CampaignContactActBlueImportArgs;
  displayErrorToast: (message: string, errors: unknown) => void;
  resetContactSourceSettings: (() => void) | null;
}): ReactElement {
  const [startImport] = useMutation<
    ImportCampaignContactsFromActBlueImportMutation,
    ImportCampaignContactsFromActBlueImportMutationVariables
  >(ACT_BLUE_IMPORT_CAMPAIGN_CONTACT_IMPORT);

  const startActBlueImport = useCallback(
    async ({
      integrationId,
      reportStartDate,
      reportEndDate,
      filters,
    }: {
      integrationId: string;
      reportStartDate: DateTime;
      reportEndDate: DateTime;
      filters: ActBlueImportFilterInput[];
    }) => {
      const { errors } = await startImport({
        variables: {
          input: {
            campaignId: campaign.id,
            integrationId,
            reportStartDate: reportStartDate.toISODate() as string,
            reportEndDate: reportEndDate.toISODate() as string,
            filters,
          },
        },
      });

      if (errors) {
        displayErrorToast("Failed to start ActBlue import", errors);
      }
    },
    [campaign, startImport, displayErrorToast]
  );

  const CampaignContactActBlueImportArgsSchema = z.object({
    campaignId: z.string(),
    integrationId: z.string(),
    reportStartDate: z.string(),
    reportEndDate: z.string(),
    filters: z.array(
      z.object({
        refcodes: z.array(z.string()),
        deviceType: z.string().nullable(),
        donationType: z.string().nullable(),
        donationMaximum: z.number().nullable(),
        donationMinimum: z.number().nullable(),
      })
    ),
  });

  const initialValues = {
    campaignId: campaign.id,
    integrationId: availableActBlueImportIntegrations[0]?.id,
    reportStartDate: DateTime.now().minus({ days: 179 }),
    reportEndDate: DateTime.now(),
    filters: [blankActBlueFilter],
  };

  if (previousJobArgs) {
    const parsedJobArgs =
      CampaignContactActBlueImportArgsSchema.safeParse(previousJobArgs);

    if (parsedJobArgs.success) {
      merge(initialValues, {
        integrationId: parsedJobArgs.data.integrationId,
        reportStartDate: DateTime.fromISO(parsedJobArgs.data.reportStartDate),
        reportEndDate: DateTime.fromISO(parsedJobArgs.data.reportEndDate),
        filters: filtersForForm(parsedJobArgs.data.filters),
      });
    }
  }

  function filtersForForm(
    filters: ActBlueImportFilter[]
  ): ActBlueImportFilterForForm[] {
    return filters.map(filter => ({
      donationMinimum: filter.donationMinimum
        ? String(filter.donationMinimum)
        : "",
      donationMaximum: filter.donationMaximum
        ? String(filter.donationMaximum)
        : "",
      deviceType: filter.deviceType || "",
      donationType: filter.donationType || "",
      refcodes: filter.refcodes ? filter.refcodes.join(", ") : "",
    }));
  }

  return (
    <ImportForm
      onSubmit={readOnly ? undefined : startActBlueImport}
      availableActBlueImportIntegrations={availableActBlueImportIntegrations}
      initialValues={initialValues}
      readOnly={readOnly}
      resetContactSourceSettings={resetContactSourceSettings}
    />
  );
}
